// Controlled search input. The `/` key from anywhere in the app focuses it.

import { useEffect, useRef } from "react";

interface Props {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  ariaLabel?: string;
}

export function SearchBox({
  value,
  onChange,
  placeholder = "Search…",
  ariaLabel = "Search",
}: Props): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key !== "/") return;
      const target = e.target as HTMLElement | null;
      // Don't hijack typing inside other inputs.
      if (target && /input|textarea|select/i.test(target.tagName)) return;
      e.preventDefault();
      inputRef.current?.focus();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className="input pr-16"
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-400 font-mono hidden sm:inline">
        /
      </span>
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          className="absolute right-8 top-1/2 -translate-y-1/2 text-ink-300 hover:text-ink-100 text-sm"
        >
          ×
        </button>
      )}
    </div>
  );
}
